/**
 * =============================================================================
 * IDS CRM Web - Schedule Engineer Work Form
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import BackToList, { useBackToList } from '../components/BackToList';
import { useAuth } from '../context/AuthContext';
import { getCapabilities } from '../utils/permissions';
import EngineerPicker, { moveToFront } from '../components/EngineerPicker';
import AssetPicker from '../components/AssetPicker';
import CustomerSearchSelect from '../components/CustomerSearchSelect';
import { useConfirm } from '../components/ConfirmDialog';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { hoursToMinutes, minutesToHours } from '../utils/duration';
import { dateKeyOf } from '../utils/dates';
import { filesToAttachmentPayload, savedAttachmentsOnly } from '../utils/attachments';
import { shortDayLabel, returnVisitCovering } from '../utils/returnVisits';
import './TaskForm.css';

// New work is booked from Jobs & Tickets and from Scheduling: Back and Cancel
// return to the list it was started from, as that list was left.
const WORK_LISTS = { '/tickets': 'Jobs & Tickets', '/tasks': 'Scheduling' };

// '2 Oct' lives with the return-visit helpers now; still exported from here
// for the booking-clash sentences and their tests.
export { shortDayLabel };

function toLocalDateTimeParts(dateValue, timeValue) {
  if (!dateValue) return { date: '', time: '' };
  const date = String(dateValue).split('T')[0];
  return { date, time: timeValue ? String(timeValue).substring(0, 5) : '' };
}

/**
 * The job has one description column. The save stores the work instructions
 * and the notes in it as 'instructions\n\nNotes: notes', so the edit page has
 * to take them apart again at the first 'Notes:' marker. Until 2026-09-22 the
 * load put the whole column into Notes as well, and every save doubled it.
 */
export const NOTES_MARKER = '\n\nNotes: ';

export function splitDescriptionAndNotes(stored) {
  const text = stored || '';
  const markerAt = text.indexOf(NOTES_MARKER);
  if (markerAt === -1) return { description: text, notes: '' };
  return {
    description: text.slice(0, markerAt),
    notes: text.slice(markerAt + NOTES_MARKER.length)
  };
}

/**
 * Several robots on one job (Phase 3): the robots a saved job holds, primary
 * first. GET /tasks/:id returns them as assets[] with is_primary; an older
 * response with only asset_id still opens as a one-robot job.
 */
export function savedAssetIds(data) {
  const listed = Array.isArray(data.assets) ? data.assets.filter(entry => entry && entry.asset_id) : [];
  if (listed.length === 0) return data.asset_id ? [data.asset_id] : [];
  const primaryId = (listed.find(entry => entry.is_primary) || {}).asset_id || data.asset_id || null;
  const ids = listed.map(entry => entry.asset_id);
  if (!primaryId || !ids.includes(primaryId)) return ids;
  return [primaryId, ...ids.filter(assetId => assetId !== primaryId)];
}

/**
 * Several engineers on one job (Phase 5): the engineers a saved job holds,
 * lead first. GET /tasks/:id returns them as engineers[] with is_lead; an
 * older response with only assigned_to_id still opens with that one engineer.
 */
export function savedEngineerIds(data) {
  const listed = Array.isArray(data.engineers) ? data.engineers.filter(entry => entry && entry.user_id) : [];
  if (listed.length === 0) return data.assigned_to_id ? [data.assigned_to_id] : [];
  const leadId = (listed.find(entry => entry.is_lead) || {}).user_id || data.assigned_to_id || null;
  const ids = listed.map(entry => entry.user_id);
  if (!leadId || !ids.includes(leadId)) return ids;
  return [leadId, ...ids.filter(userId => userId !== leadId)];
}

/**
 * Double-booking warnings (Phase 6 step 3; Gareth 23 Sep: a WARNING, never a
 * block). GET /api/v1/tasks/booking-clashes answers with facts, grouped by
 * engineer and by robot; the sentences are written here. Saving is never
 * refused because of a clash.
 */
const NO_CLASHES = { engineers: {}, assets: {} };
const CLASH_DEBOUNCE_MS = 300;
// At most this many robot warnings under one robot, then "and N more".
const ROBOT_CLASHES_SHOWN = 3;
const TIME_PATTERN = /^\d{2}:\d{2}/;

const clockOf = (value) => (value && TIME_PATTERN.test(String(value)) ? String(value).substring(0, 5) : '');

/**
 * The sentence for one booking an engineer already has. formDate is the day
 * being planned: a clash that starts on another day (a job running past
 * midnight) says which day it starts.
 */
export function engineerClashSentence(clash, formDate) {
  if (!clash) return '';
  const ref = clash.ref || 'another job';
  const where = clash.customer_name ? `, ${clash.customer_name}` : '';
  if (clash.kind === 'maintenance') {
    return `Already has maintenance ${ref}${clash.customer_name ? ` at ${clash.customer_name}` : ''} that day`;
  }
  if (clash.all_day) return `Already booked all day on ${ref}${where}`;
  const start = clockOf(clash.start_time);
  if (!start) return `Already on ${ref}${where} that day (no start time set)`;
  const end = clockOf(clash.end_time);
  const clashDay = dateKeyOf(clash.date);
  const otherDay = clashDay && formDate && clashDay !== formDate ? ` (from ${shortDayLabel(clashDay)})` : '';
  const span = end ? `${start} to ${end}` : `from ${start}`;
  return `Already booked ${span}${otherDay} on ${ref}${where}`;
}

/** The sentences for a robot already on other open jobs: at most three, then "and N more". */
export function robotClashSentences(list) {
  const clashes = Array.isArray(list) ? list.filter(Boolean) : [];
  const shown = clashes.slice(0, ROBOT_CLASHES_SHOWN).map(clash => {
    const ref = clash.ref || 'another job';
    const day = shortDayLabel(clash.date);
    return day ? `Already on open job ${ref}, booked for ${day}` : `Already on open job ${ref}`;
  });
  const more = clashes.length - shown.length;
  if (more > 0) shown.push(`and ${more} more`);
  return shown;
}

const groupedClashes = (value) => (value && typeof value === 'object' && !Array.isArray(value) ? value : {});

/**
 * Saved Log Work (Phase 6 step 7, spec 6.5). Saving an edit that takes a
 * robot off the job, or changes the job type, sets aside the check form
 * answers already typed into the job's unfinished Log Work for that robot:
 * the server keeps them for reference but they are never filed. The edit
 * page asks first. logWork is GET /tasks/:id/log-work; anything but an
 * answer with available: true (the store not ready, an old server, a failed
 * read) means no warning, and the edit saves as before.
 *
 * Returns the question to ask, or null.
 */
const lowerId = (value) => String(value || '').toLowerCase();

export function logWorkEditWarning(logWork, {
  assetIds = [], originalTaskType = null, taskType = null, assetRefs = {}
} = {}) {
  const parts = logWork && logWork.available === true && logWork.draft && logWork.draft.parts;
  if (!parts || typeof parts !== 'object') return null;
  const kept = new Set((assetIds || []).map(lowerId));
  const typeChanged = Boolean(originalTaskType) && Boolean(taskType) && originalTaskType !== taskType;
  const refs = {};
  Object.entries(assetRefs || {}).forEach(([assetId, ref]) => { if (ref) refs[lowerId(assetId)] = ref; });

  const affected = [];
  Object.entries(parts).forEach(([partKey, part]) => {
    const isRobot = partKey.startsWith('robot:');
    if (!isRobot && partKey !== 'form:task') return;
    const form = part && part.value && part.value.form;
    const answers = form && typeof form === 'object' ? form.answers : null;
    if (!answers || typeof answers !== 'object' || Object.keys(answers).length === 0) return;
    const assetId = isRobot ? lowerId(partKey.slice(6)) : null;
    const takenOff = Boolean(assetId) && !kept.has(assetId);
    const otherType = typeChanged && form.task_type !== taskType;
    if (!takenOff && !otherType) return;
    affected.push({
      label: assetId ? (refs[assetId] || 'A robot') : 'This job',
      by: (part && part.changed_by_name) || 'another engineer'
    });
  });
  if (affected.length === 0) return null;

  const ending = 'They will be kept for reference but not filed. Save anyway?';
  if (affected.length === 1) {
    return `${affected[0].label} has check form answers in unfinished Log Work by ${affected[0].by}. ${ending}`;
  }
  const named = affected.map(item => `${item.label} (${item.by})`);
  const listed = `${named.slice(0, -1).join(', ')} and ${named[named.length - 1]}`;
  return `${listed} have check form answers in unfinished Log Work. ${ending}`;
}

/**
 * Book a return visit (Phase 6 step 5). /tasks/new?return_for=<job> fills
 * the form in from a finished job with robots left Not done: the same
 * customer, route, work type and priority, the not-done robots (primary
 * first) at the site they are at NOW, a title and instructions naming each
 * robot and why it was not done. Date, time and engineers are left blank for
 * the planner (Gareth 23 Sep). Left out, each with a note:
 *   - a robot already on a return visit from that job (not cancelled);
 *   - a robot that now belongs to another customer;
 *   - a robot now at a different site from the rest (book it separately).
 * The site is the job's own site while any robot is still there, otherwise
 * the site the first robot is at now. GET /tasks/:id gives each robot's
 * current customer_id and farm_site_id (listTaskMembers).
 */
const RETURN_VISIT_TASK_TYPES = ['drop_in', 'quarterly_health_check', 'annual_service', 'remote_check_in', 'upgrade'];
const RETURN_VISIT_PRIORITIES = ['low', 'normal', 'high', 'critical'];
const TITLE_MAX = 255;

export function planReturnVisit(source) {
  const ref = source?.task_ref || 'this job';
  if (!source || source.status !== 'complete' || source.task_type === 'sales_action') {
    return { refusal: 'A return visit can only be booked for a finished job that is not a sales job.' };
  }
  const listed = (Array.isArray(source.assets) ? source.assets : []).filter(row => row && row.asset_id);
  const rowsById = Object.fromEntries(listed.map(row => [row.asset_id, row]));
  const ordered = savedAssetIds(source).map(assetId => rowsById[assetId]).filter(Boolean);
  const notDone = ordered.filter(row => row.visit_outcome === 'not_done');
  if (notDone.length === 0) {
    return { refusal: `Every robot on ${ref} was done on that visit, so there is nothing to go back for.` };
  }

  const leftOut = [];
  const candidates = [];
  notDone.forEach(row => {
    const assetRef = row.asset_ref || 'A robot';
    const booked = returnVisitCovering(source, row.asset_id);
    if (booked) {
      leftOut.push({ asset_id: row.asset_id, asset_ref: assetRef, kind: 'booked', visit: booked });
      return;
    }
    if (row.customer_id && source.customer_id && row.customer_id !== source.customer_id) {
      leftOut.push({ asset_id: row.asset_id, asset_ref: assetRef, kind: 'moved_customer' });
      return;
    }
    candidates.push(row);
  });

  const jobSite = source.farm_site_id || '';
  const siteOf = (row) => row.farm_site_id || jobSite;
  const targetSite = candidates.some(row => siteOf(row) === jobSite)
    ? jobSite
    : (candidates.length > 0 ? siteOf(candidates[0]) : jobSite);
  const included = [];
  candidates.forEach(row => {
    if (siteOf(row) === targetSite) {
      included.push(row);
    } else {
      leftOut.push({ asset_id: row.asset_id, asset_ref: row.asset_ref || 'A robot', kind: 'moved_site', farm_site_id: row.farm_site_id });
    }
  });

  const refs = included.map(row => row.asset_ref || 'robot');
  const title = refs.length > 0
    ? `Return visit - ${ref} - ${refs[0]}${refs.length > 1 ? ` +${refs.length - 1}` : ''}`
    : `Return visit - ${ref}`;
  const instructions = included.length > 0
    ? [
      `Return visit for ${ref}. Robots not done on that visit:`,
      ...included.map(row => `- ${row.asset_ref || 'Robot'}: ${String(row.outcome_note || '').trim() || 'no reason given'}`)
    ].join('\n')
    : `Return visit for ${ref}.`;

  const sourceType = source.source_type === 'distributor' ? 'distributor' : 'direct';
  const knownRefs = {};
  const followUps = {};
  included.forEach(row => {
    knownRefs[row.asset_id] = { asset_ref: row.asset_ref, robot_type: row.robot_type, serial_number: row.serial_number };
    if (row.follow_up_ticket_id || row.follow_up_ticket_ref) {
      followUps[row.asset_id] = row.follow_up_ticket_ref || 'a ticket';
    }
  });

  return {
    form: {
      source_type: sourceType,
      distributor_id: sourceType === 'distributor' ? (source.distributor_id || '') : '',
      customer_id: source.customer_id || '',
      farm_site_id: targetSite,
      asset_ids: included.map(row => row.asset_id),
      use_non_listed_asset: false,
      non_listed_asset_label: '',
      title: title.slice(0, TITLE_MAX),
      description: instructions,
      task_type: RETURN_VISIT_TASK_TYPES.includes(source.task_type) ? source.task_type : 'drop_in',
      priority: RETURN_VISIT_PRIORITIES.includes(source.priority) ? source.priority : 'normal',
      scheduled_date: '',
      scheduled_start_time: '',
      is_all_day: false,
      assignment_target: 'engineer',
      engineer_ids: []
    },
    knownRefs,
    returnVisit: {
      task_id: source.task_id,
      task_ref: source.task_ref || null,
      leftOut,
      followUps
    }
  };
}

/** The sentence for a robot the return visit leaves out. siteName names a site by id. */
export function leftOutSentence(entry, siteName = () => '') {
  if (!entry) return '';
  if (entry.kind === 'booked') {
    const day = shortDayLabel(entry.visit?.scheduled_date);
    return `${entry.asset_ref} is already booked on ${entry.visit?.task_ref || 'a return visit'}${day ? ` for ${day}` : ''}, so it is left out.`;
  }
  if (entry.kind === 'moved_customer') {
    return `${entry.asset_ref} has moved to another customer - book it separately.`;
  }
  return `${entry.asset_ref} has moved to ${siteName(entry.farm_site_id) || 'another site'} - book it separately.`;
}

function TaskFormBody() {
  const { guardElement, markDirty } = useUnsavedChanges();
  const { confirmElement, requestConfirm } = useConfirm();
  const { id } = useParams();
  const navigate = useNavigate();
  const backToList = useBackToList('/tickets', ['/tasks']);
  const [searchParams] = useSearchParams();
  const { authFetch } = useAuth();
  const isEdit = Boolean(id);
  const initialCustomerId = searchParams.get('customer_id') || searchParams.get('customer') || '';
  const initialAssetId = searchParams.get('asset_id') || searchParams.get('asset') || '';
  // Book a return visit (Phase 6 step 5): the finished job it goes back to.
  // New work only - the edit page never reads it.
  const returnForId = isEdit ? '' : (searchParams.get('return_for') || '');

  // While a return visit is being filled in the page shows Loading, so no
  // customer or site change can wipe the pre-fill as it lands.
  const [loading, setLoading] = useState(isEdit || Boolean(returnForId));
  // { task_id, task_ref, leftOut, followUps } once a return visit is filled in.
  const [returnVisit, setReturnVisit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [attachmentError, setAttachmentError] = useState('');
  const [customerLabel, setCustomerLabel] = useState('');
  const [distributorLabel, setDistributorLabel] = useState('');
  const [sites, setSites] = useState([]);
  const [assets, setAssets] = useState([]);
  // Details for robots on the job that the site's list does not offer (a
  // robot that has moved site, or one awaiting install), so their rows still
  // read properly in the picker.
  const [knownRefs, setKnownRefs] = useState({});
  const [engineers, setEngineers] = useState([]);
  // Names for engineers on a saved job who are no longer in the active list,
  // so their rows still read properly in the picker.
  const [knownEngineerNames, setKnownEngineerNames] = useState({});
  // The edit page only: the robots and job type the job had when it opened,
  // so saving can tell what the edit takes away (saved Log Work warning).
  const [loadedJob, setLoadedJob] = useState({ assetIds: [], taskType: null });

  const [form, setForm] = useState({
    source_type: 'direct',
    distributor_id: '',
    customer_id: '',
    farm_site_id: '',
    // The robots on the job in the order added; the first is the primary
    // robot and keeps the job's single asset slot.
    asset_ids: [],
    use_non_listed_asset: false,
    non_listed_asset_label: '',
    title: '',
    description: '',
    task_type: 'drop_in',
    scheduled_date: '',
    scheduled_start_time: '',
    // Entered in hours (Gareth 2026-09-21); stored in minutes - utils/duration.
    estimated_duration_hours: '1',
    is_all_day: false,
    priority: 'normal',
    assignment_target: 'engineer',
    // The engineers on the job in the order added; the first is the lead and
    // keeps the job's single engineer slot (assigned_to_id).
    engineer_ids: [],
    notes: '',
    attachments: []
  });

  // L8: advisory availability warnings (absence, standby, off duty) for each
  // engineer on the job on the planned date, shown beside their name. One call
  // per engineer, re-run when the crew or the date changes. The key is sorted
  // so that only changing the lead does not ask again.
  const [availability, setAvailability] = useState({});
  const availabilityKey = form.assignment_target === 'engineer' ? [...form.engineer_ids].sort().join(',') : '';

  useEffect(() => {
    let cancelled = false;
    const userIds = availabilityKey ? availabilityKey.split(',') : [];
    if (userIds.length === 0) {
      setAvailability({});
      return undefined;
    }
    const dateParam = form.scheduled_date ? `?date=${form.scheduled_date}` : '';
    const warningsFor = async (userId) => {
      try {
        const response = await authFetch(`/api/v1/engineers/${userId}/availability${dateParam}`);
        if (!response.ok) return [userId, []];
        const data = await response.json();
        return [userId, Array.isArray(data.warnings) ? data.warnings : []];
      } catch (err) {
        return [userId, []];
      }
    };
    Promise.all(userIds.map(warningsFor)).then(entries => {
      if (!cancelled) setAvailability(Object.fromEntries(entries));
    });
    return () => { cancelled = true; };
  }, [authFetch, availabilityKey, form.scheduled_date]);

  // Double-booking warnings (Phase 6 step 3): one call for the whole job,
  // separate from the availability check above (whose URL stays as it is),
  // and asked about 300 ms after the last change so typing a time does not
  // fire a call per key. Nothing is asked without a date. Engineers are asked
  // only when the job names engineers (a planning-group job asks robots only);
  // robots only when the job is not a non-listed asset. On the edit page the
  // job itself is left out, so it never clashes with its own booking.
  const [clashes, setClashes] = useState(NO_CLASHES);
  const clashAssetKey = form.use_non_listed_asset ? '' : [...form.asset_ids].sort().join(',');
  const clashStartTime = form.is_all_day ? '' : clockOf(form.scheduled_start_time);
  const clashDurationMins = hoursToMinutes(form.estimated_duration_hours);
  const clashExcludeId = isEdit ? id : '';

  useEffect(() => {
    let cancelled = false;
    const date = dateKeyOf(form.scheduled_date);
    if (!date || (!availabilityKey && !clashAssetKey)) {
      setClashes(NO_CLASHES);
      return undefined;
    }
    const params = [`date=${date}`];
    if (clashStartTime) params.push(`start_time=${clashStartTime}`);
    if (!form.is_all_day && Number.isInteger(clashDurationMins) && clashDurationMins >= 1 && clashDurationMins <= 1440) {
      params.push(`duration_mins=${clashDurationMins}`);
    }
    params.push(`all_day=${form.is_all_day ? 'true' : 'false'}`);
    if (availabilityKey) params.push(`engineer_ids=${availabilityKey.split(',').map(encodeURIComponent).join(',')}`);
    if (clashAssetKey) params.push(`asset_ids=${clashAssetKey.split(',').map(encodeURIComponent).join(',')}`);
    if (clashExcludeId) params.push(`exclude_task_id=${encodeURIComponent(clashExcludeId)}`);

    const timer = setTimeout(async () => {
      try {
        const response = await authFetch(`/api/v1/tasks/booking-clashes?${params.join('&')}`);
        if (!response.ok) {
          if (!cancelled) setClashes(NO_CLASHES);
          return;
        }
        const data = await response.json();
        if (!cancelled) {
          setClashes({ engineers: groupedClashes(data && data.engineers), assets: groupedClashes(data && data.assets) });
        }
      } catch (err) {
        // A warning that cannot be fetched is simply not shown.
        if (!cancelled) setClashes(NO_CLASHES);
      }
    }, CLASH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [authFetch, availabilityKey, clashAssetKey, clashStartTime, clashDurationMins, clashExcludeId, form.is_all_day, form.scheduled_date]);

  const warningChips = (warnings, extraStyle) => (
    <div className="availability-warnings" style={extraStyle}>
      {warnings.map((warning, index) => (
        <span key={index} className="availability-warning-chip">⚠ {warning}</span>
      ))}
    </div>
  );

  // Under each engineer: leave / standby / off duty first, then any job or
  // maintenance they are already booked on.
  const availabilityNote = (userId) => {
    const bookedOn = Array.isArray(clashes.engineers[userId]) ? clashes.engineers[userId] : [];
    const warnings = [
      ...(availability[userId] || []),
      ...bookedOn.map(clash => engineerClashSentence(clash, form.scheduled_date)).filter(Boolean)
    ];
    if (warnings.length === 0) return null;
    return warningChips(warnings);
  };

  // Under each robot: a follow-up ticket already raised for it on the job a
  // return visit is for, then the other open jobs it is already on, any date.
  const robotClashNote = (assetId) => {
    const followUpRef = returnVisit?.followUps?.[assetId];
    const warnings = [
      ...(followUpRef ? [`Follow-up ${followUpRef} already raised`] : []),
      ...robotClashSentences(clashes.assets[assetId])
    ];
    if (warnings.length === 0) return null;
    return warningChips(warnings, { marginTop: 0 });
  };

  const priorities = [
    { value: 'low', label: 'Low' },
    { value: 'normal', label: 'Normal' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' }
  ];

  const taskTypes = [
    { value: 'drop_in', label: 'Planned Site Visit' },
    // Maintenance visits: Log Work on these jobs uses the scheduled
    // maintenance check form for the visit when one is set up.
    { value: 'quarterly_health_check', label: 'Quarterly Health Check' },
    { value: 'annual_service', label: 'Annual Service' },
    { value: 'remote_check_in', label: 'Remote Engineer Work' },
    { value: 'upgrade', label: 'Upgrade / Project Work' }
  ];

  const fetchAssets = useCallback(async (customerId) => {
    if (!customerId) {
      setAssets([]);
      return;
    }
    try {
      const response = await authFetch(`/api/v1/assets?customer_id=${customerId}&limit=100`);
      const data = await response.json();
      // Assets awaiting install aren't in service yet - no tasks against them
      setAssets((data.data || []).filter(a => a.status !== 'pending_install'));
    } catch (err) {
      console.error('Failed to fetch assets:', err);
    }
  }, [authFetch]);

  const fetchSites = useCallback(async (customerId) => {
    if (!customerId) {
      setSites([]);
      return;
    }
    try {
      const response = await authFetch(`/api/v1/customers/${customerId}/sites`);
      const data = await response.json();
      setSites(data.data || []);
    } catch (err) {
      console.error('Failed to fetch sites:', err);
    }
  }, [authFetch]);

  const fetchEngineers = useCallback(async () => {
    try {
      const response = await authFetch('/api/v1/tickets/active-engineers');
      const data = await response.json();
      setEngineers(data.data || []);
    } catch (err) {
      console.error('Failed to fetch engineers:', err);
    }
  }, [authFetch]);

  const fetchTask = useCallback(async () => {
    if (!isEdit) return;
    try {
      const response = await authFetch(`/api/v1/tasks/${id}`);
      if (!response.ok) throw new Error('Task not found');
      const data = await response.json();
      const dateParts = toLocalDateTimeParts(data.scheduled_date, data.scheduled_start_time);
      const storedText = splitDescriptionAndNotes(data.description);

      setForm({
        source_type: data.source_type || 'direct',
        distributor_id: data.distributor_id || '',
        customer_id: data.customer_id || '',
        farm_site_id: data.farm_site_id || '',
        asset_ids: savedAssetIds(data),
        use_non_listed_asset: Boolean(data.non_listed_asset_label),
        non_listed_asset_label: data.non_listed_asset_label || '',
        title: data.title || '',
        description: storedText.description,
        task_type: data.task_type || 'drop_in',
        scheduled_date: dateParts.date,
        scheduled_start_time: dateParts.time,
        estimated_duration_hours: minutesToHours(data.estimated_duration_mins) || '1',
        is_all_day: data.is_all_day || false,
        priority: data.priority || 'normal',
        assignment_target: data.assigned_group === 'maintenance_planning_owner' ? 'group' : 'engineer',
        engineer_ids: data.assigned_group === 'maintenance_planning_owner' ? [] : savedEngineerIds(data),
        notes: storedText.notes,
        attachments: data.attachments || []
      });
      setLoadedJob({
        assetIds: data.non_listed_asset_label ? [] : savedAssetIds(data),
        taskType: data.task_type || null
      });
      setCustomerLabel(data.customer_name || '');
      setDistributorLabel(data.distributor_name || '');
      const saved = {};
      (Array.isArray(data.assets) ? data.assets : []).forEach(entry => {
        if (entry && entry.asset_id) {
          saved[entry.asset_id] = { asset_ref: entry.asset_ref, robot_type: entry.robot_type, serial_number: entry.serial_number };
        }
      });
      if (data.asset_id && !saved[data.asset_id] && data.asset_ref) saved[data.asset_id] = { asset_ref: data.asset_ref };
      setKnownRefs(saved);
      const names = {};
      (Array.isArray(data.engineers) ? data.engineers : []).forEach(entry => {
        if (entry && entry.user_id) names[entry.user_id] = entry.full_name || entry.preferred_name || '';
      });
      if (data.assigned_to_id && !names[data.assigned_to_id] && data.assigned_to_name) {
        names[data.assigned_to_id] = data.assigned_to_name;
      }
      setKnownEngineerNames(names);

      if (data.customer_id) {
        fetchAssets(data.customer_id);
        fetchSites(data.customer_id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, id, isEdit, fetchAssets, fetchSites]);

  useEffect(() => {
    fetchEngineers();
    fetchTask();
  }, [fetchEngineers, fetchTask]);

  // Book a return visit: fill the form in from the finished job (see
  // planReturnVisit). Only on new work, and only once.
  useEffect(() => {
    if (!returnForId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await authFetch(`/api/v1/tasks/${encodeURIComponent(returnForId)}`);
        if (!response.ok) throw new Error('The job to book a return visit for could not be found.');
        const source = await response.json();
        if (cancelled) return;
        const plan = planReturnVisit(source);
        if (plan.refusal) {
          setError(plan.refusal);
          return;
        }
        setForm(prev => ({ ...prev, ...plan.form }));
        setCustomerLabel(source.customer_name || '');
        setDistributorLabel(plan.form.source_type === 'distributor' ? (source.distributor_name || '') : '');
        setKnownRefs(prev => ({ ...prev, ...plan.knownRefs }));
        setReturnVisit(plan.returnVisit);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch, returnForId]);

  useEffect(() => {
    if (isEdit || returnForId || (!initialCustomerId && !initialAssetId)) return;

    const fetchInitialContext = async () => {
      try {
        if (initialAssetId) {
          const response = await authFetch(`/api/v1/assets/${initialAssetId}`);
          if (!response.ok) return;
          const asset = await response.json();
          setForm(prev => ({
            ...prev,
            customer_id: asset.customer_id || prev.customer_id,
            farm_site_id: asset.farm_site_id || prev.farm_site_id,
            asset_ids: asset.asset_id ? [asset.asset_id] : prev.asset_ids,
            use_non_listed_asset: false,
            non_listed_asset_label: ''
          }));
          if (asset.asset_id) {
            setKnownRefs(prev => ({
              ...prev,
              [asset.asset_id]: { asset_ref: asset.asset_ref, robot_type: asset.robot_type, serial_number: asset.serial_number }
            }));
          }
          setCustomerLabel(asset.customer_name || '');
          return;
        }
        setForm(prev => ({ ...prev, customer_id: initialCustomerId }));
        const customerResponse = await authFetch(`/api/v1/customers/${initialCustomerId}`);
        if (customerResponse.ok) {
          const customer = await customerResponse.json();
          setCustomerLabel(customer.business_name || '');
        }
      } catch (err) {
        console.error('Failed to load initial task context:', err);
      }
    };

    fetchInitialContext();
  }, [authFetch, initialAssetId, initialCustomerId, isEdit, returnForId]);

  useEffect(() => {
    if (form.customer_id) {
      fetchAssets(form.customer_id);
      fetchSites(form.customer_id);
    }
  }, [form.customer_id, fetchAssets, fetchSites]);

  // A site's name as the Site dropdown shows it; '' for a site not in the list.
  const siteNameOf = (siteId) => {
    const site = sites.find(entry => entry.site_id === siteId);
    return site ? (site.site_name || `Site ${site.site_number || ''}`.trim()) : '';
  };

  const filteredAssets = form.farm_site_id
    ? assets.filter(asset => asset.farm_site_id === form.farm_site_id)
    : assets;

  const handleChange = async (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'file') {
      const picker = e.target;
      setAttachmentError('');
      try {
        const picked = await filesToAttachmentPayload(files);
        // The picker replaces its own selection, so a new pick stands in for
        // any earlier unsaved pick. Files already saved with the job are not in
        // the picker and stay attached: on the edit page they must not be lost.
        setForm(prev => ({ ...prev, attachments: [...savedAttachmentsOnly(prev.attachments), ...picked] }));
      } catch (err) {
        // Refused files are not attached. The picker is emptied so it cannot go
        // on showing files that will not be saved with the work, and the reason
        // is shown beside it: the banner at the top of this form is off screen.
        picker.value = '';
        setForm(prev => ({ ...prev, attachments: savedAttachmentsOnly(prev.attachments) }));
        setAttachmentError(err.message);
      }
      return;
    }

    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'source_type' && value === 'direct' ? { distributor_id: '' } : {}),
      // Robots are picked per site: a different site means a different list.
      ...(name === 'farm_site_id' ? { asset_ids: [] } : {}),
      // Non-listed Asset is either/or with listed robots.
      ...(name === 'use_non_listed_asset' && checked ? { asset_ids: [] } : {}),
      // Named engineers or the planning group, never both.
      ...(name === 'assignment_target' && value === 'group' ? { engineer_ids: [] } : {})
    }));
    if (name === 'source_type' && value === 'direct') setDistributorLabel('');
  };

  // The robots on the job, from the picker. Adding a robot unticks Non-listed
  // Asset: a job is either listed robots or one non-listed asset, never both.
  const handleAssetsChange = (ids) => {
    markDirty();
    setForm(prev => ({
      ...prev,
      asset_ids: ids,
      ...(ids.length > 0 ? { use_non_listed_asset: false, non_listed_asset_label: '' } : {})
    }));
  };

  // The engineers on the job, from the picker; the first is the lead.
  const handleEngineersChange = (ids) => {
    markDirty();
    setForm(prev => ({ ...prev, engineer_ids: ids }));
  };

  const handleMakeLead = (userId) => {
    markDirty();
    setForm(prev => ({ ...prev, engineer_ids: moveToFront(prev.engineer_ids, userId) }));
  };

  const handleCustomerSelect = (row) => {
    markDirty();
    setForm(prev => ({
      ...prev,
      customer_id: row ? row.customer_id : '',
      farm_site_id: '',
      asset_ids: []
    }));
    setCustomerLabel(row ? row.business_name : '');
    if (!row) {
      setSites([]);
      setAssets([]);
    }
  };

  const handleDistributorSelect = (row) => {
    markDirty();
    // A different distributor means a different customer pool - drop any
    // customer already picked so a mismatched pairing can't be saved.
    setForm(prev => ({
      ...prev,
      distributor_id: row ? row.customer_id : '',
      customer_id: '',
      farm_site_id: '',
      asset_ids: []
    }));
    setDistributorLabel(row ? row.business_name : '');
    setCustomerLabel('');
    setSites([]);
    setAssets([]);
  };

  const validate = () => {
    if (form.source_type === 'distributor' && !form.distributor_id) return 'Please select a distributor';
    if (!form.customer_id) return 'Please select a customer';
    if (!form.farm_site_id) return 'Please select a site';
    if (form.asset_ids.length === 0 && !form.non_listed_asset_label.trim()) return 'Add at least one robot or choose Non-listed Asset';
    if (!form.title.trim()) return 'Please enter a work title';
    if (!form.description.trim()) return 'Please enter work instructions';
    if (!form.scheduled_date) return 'Please select a planned date';
    if (form.assignment_target === 'engineer' && form.engineer_ids.length === 0) return 'Add at least one engineer or choose Maintenance Planning Owner group';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      setSaving(false);
      return;
    }

    try {
      const payload = {
        source_type: form.source_type,
        distributor_id: form.source_type === 'distributor' ? form.distributor_id : null,
        customer_id: form.customer_id,
        farm_site_id: form.farm_site_id,
        // Every robot on the job, primary first; asset_id carries the primary
        // so the single-asset slot and older readers keep working.
        asset_ids: form.use_non_listed_asset ? [] : form.asset_ids,
        asset_id: form.use_non_listed_asset ? null : (form.asset_ids[0] || null),
        non_listed_asset_label: form.use_non_listed_asset ? form.non_listed_asset_label.trim() : null,
        task_type: form.task_type,
        title: form.title.trim(),
        description: [form.description, form.notes ? `Notes: ${form.notes}` : ''].filter(Boolean).join('\n\n'),
        scheduled_date: form.scheduled_date,
        scheduled_start_time: form.is_all_day ? null : (form.scheduled_start_time || null),
        estimated_duration_mins: hoursToMinutes(form.estimated_duration_hours) ?? undefined,
        is_all_day: form.is_all_day,
        priority: form.priority,
        // Every engineer on the job, lead first; assigned_to_id carries the
        // lead so the single engineer slot and older readers keep working.
        engineer_ids: form.assignment_target === 'engineer' ? form.engineer_ids : [],
        assigned_to_id: form.assignment_target === 'engineer' ? (form.engineer_ids[0] || null) : null,
        assigned_group: form.assignment_target === 'group' ? 'maintenance_planning_owner' : null,
        attachments: form.attachments,
        // A return visit names the finished job it goes back to, on create
        // only: the server checks it and PUT does not accept it.
        ...(!isEdit && returnVisit?.task_id ? { return_visit_of_task_id: returnVisit.task_id } : {})
      };

      // Saved Log Work: an edit that takes robots off or changes the job
      // type asks first when someone's check form answers would be set aside.
      const warning = isEdit ? await unfinishedLogWorkWarning(payload) : null;
      if (warning) {
        requestConfirm({
          title: 'Unfinished Log Work on this job',
          message: warning,
          confirmLabel: 'Save anyway',
          cancelLabel: 'Go back',
          onConfirm: () => { sendSave(payload); },
          onCancel: () => setSaving(false)
        });
        return;
      }
      await sendSave(payload);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  /**
   * The question to ask before saving this edit, or null. Only an edit that
   * takes a robot off or changes the job type reads the saved Log Work; a
   * read that fails is no warning.
   */
  const unfinishedLogWorkWarning = async (payload) => {
    const keptIds = new Set((payload.asset_ids || []).map(lowerId));
    const takesRobotsOff = loadedJob.assetIds.some(assetId => !keptIds.has(lowerId(assetId)));
    const changesType = Boolean(loadedJob.taskType) && loadedJob.taskType !== payload.task_type;
    if (!takesRobotsOff && !changesType) return null;
    let logWork = null;
    try {
      const response = await authFetch(`/api/v1/tasks/${id}/log-work`);
      if (response && response.ok) logWork = await response.json();
    } catch (err) {
      logWork = null;
    }
    const assetRefs = {};
    assets.forEach(asset => { if (asset && asset.asset_id) assetRefs[asset.asset_id] = asset.asset_ref; });
    Object.entries(knownRefs).forEach(([assetId, known]) => {
      if (known && known.asset_ref) assetRefs[assetId] = known.asset_ref;
    });
    return logWorkEditWarning(logWork, {
      assetIds: payload.asset_ids,
      originalTaskType: loadedJob.taskType,
      taskType: payload.task_type,
      assetRefs
    });
  };

  const sendSave = async (payload) => {
    try {
      const url = isEdit ? `/api/v1/tasks/${id}` : '/api/v1/tasks';
      const method = isEdit ? 'PUT' : 'POST';
      const response = await authFetch(url, {
        method,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to save scheduled work');
      }

      const savedTask = await response.json();
      navigate(`/tasks/${savedTask.task_id}`);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  if (loading) return <div className="loading-page">Loading...</div>;

  return (
    <div className="task-form-page">
      <div className="form-header">
        <BackToList to="/tickets" also={['/tasks']} labels={WORK_LISTS} className="back-link">&larr; Jobs &amp; Tickets</BackToList>
        <h1>{isEdit ? 'Edit Scheduled Work' : 'Schedule Engineer Work'}</h1>
      </div>

      {returnVisit && (
        <div className="form-section return-visit-banner" role="status" aria-label="Return visit">
          <h3>
            Booking a return visit for{' '}
            <Link to={`/tasks/${returnVisit.task_id}`}>{returnVisit.task_ref || 'the finished job'}</Link>
          </h3>
          <p className="search-status">
            The robots not done on that visit are filled in. Choose the date, time and engineers.
          </p>
          {returnVisit.leftOut.length > 0 && warningChips(
            returnVisit.leftOut.map(entry => leftOutSentence(entry, siteNameOf))
          )}
        </div>
      )}

      {guardElement}
      {confirmElement}
      <form onChangeCapture={markDirty} onSubmit={handleSubmit} className="task-form">
        {error && <div className="form-error">{error}</div>}

        <div className="form-section">
          <h3>Customer Route</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Direct or Distributor *</label>
              <select name="source_type" value={form.source_type} onChange={handleChange}>
                <option value="direct">Direct</option>
                <option value="distributor">Distributor</option>
              </select>
            </div>

            {form.source_type === 'distributor' && (
              <div className="form-group">
                <label htmlFor="distributor_search">Distributor *</label>
                <CustomerSearchSelect
                  id="distributor_search"
                  kind="distributor"
                  value={form.distributor_id}
                  initialLabel={distributorLabel}
                  onSelect={handleDistributorSelect}
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="customer_search">Customer *</label>
              <CustomerSearchSelect
                id="customer_search"
                kind="customer"
                statusFilter="active"
                distributorId={form.source_type === 'distributor' ? form.distributor_id || null : null}
                value={form.customer_id}
                initialLabel={customerLabel}
                onSelect={handleCustomerSelect}
              />
            </div>

            <div className="form-group">
              <label>Site *</label>
              <select name="farm_site_id" value={form.farm_site_id} onChange={handleChange} required disabled={!form.customer_id}>
                <option value="">Select site...</option>
                {sites.map(site => (
                  <option key={site.site_id} value={site.site_id}>
                    {site.site_name || `Site ${site.site_number || ''}`.trim()}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Asset</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="task_asset_picker">Robots on this job *</label>
              <AssetPicker
                id="task_asset_picker"
                options={form.customer_id ? filteredAssets : []}
                selectedIds={form.asset_ids}
                onChange={handleAssetsChange}
                knownRefs={knownRefs}
                noteFor={robotClashNote}
                emptyText={form.customer_id ? 'No robot added yet.' : 'Pick a customer and site first.'}
              />
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input type="checkbox" name="use_non_listed_asset" checked={form.use_non_listed_asset} onChange={handleChange} />
                Non-listed Asset
              </label>
            </div>

            {form.use_non_listed_asset && (
              <div className="form-group">
                <label htmlFor="task_non_listed_asset_label">Non-listed asset description *</label>
                <input id="task_non_listed_asset_label" name="non_listed_asset_label" value={form.non_listed_asset_label} onChange={handleChange} maxLength={255} />
              </div>
            )}
          </div>
        </div>

        <div className="form-section">
          <h3>Work Details</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="task_type">Work type</label>
              <select id="task_type" name="task_type" value={form.task_type} onChange={handleChange}>
                {taskTypes.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>Priority</label>
              <select name="priority" value={form.priority} onChange={handleChange}>
                {priorities.map(priority => <option key={priority.value} value={priority.value}>{priority.label}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="task_title">Work title *</label>
            <input id="task_title" name="title" value={form.title} onChange={handleChange} required maxLength={255} />
          </div>

          <div className="form-group">
            <label htmlFor="task_description">Work instructions *</label>
            <textarea id="task_description" name="description" value={form.description} onChange={handleChange} rows={5} required />
          </div>
        </div>

        <div className="form-section">
          <h3>Planning</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="task_scheduled_date">Planned date *</label>
              <input id="task_scheduled_date" type="date" name="scheduled_date" value={form.scheduled_date} onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label>Start time</label>
              <input type="time" name="scheduled_start_time" value={form.scheduled_start_time} onChange={handleChange} disabled={form.is_all_day} />
            </div>

            <div className="form-group">
              <label htmlFor="task-duration-hours">Duration (hours)</label>
              <input id="task-duration-hours" type="number" name="estimated_duration_hours" value={form.estimated_duration_hours} onChange={handleChange} min="0.25" max="8" step="0.25" />
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input type="checkbox" name="is_all_day" checked={form.is_all_day} onChange={handleChange} />
              All day task
            </label>
          </div>
        </div>

        <div className="form-section">
          <h3>Assignment</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Assign to</label>
              <select name="assignment_target" value={form.assignment_target} onChange={handleChange}>
                <option value="engineer">Engineer</option>
                <option value="group">Maintenance Planning Owner group</option>
              </select>
            </div>

            {form.assignment_target === 'engineer' && (
              <div className="form-group">
                <label htmlFor="task_engineer_picker">Engineers on this job *</label>
                <EngineerPicker
                  id="task_engineer_picker"
                  options={engineers}
                  selectedIds={form.engineer_ids}
                  onChange={handleEngineersChange}
                  knownNames={knownEngineerNames}
                  emptyText="No engineer added yet."
                  leadLabel="Lead"
                  noteFor={availabilityNote}
                  onMakeLead={handleMakeLead}
                />
                {form.engineer_ids.length > 1 && (
                  <div className="search-status">The first engineer is the lead. Being on the job does not log any hours: each engineer logs their own on the day.</div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="form-section">
          <h3>Notes &amp; Attachments</h3>
          <div className="form-group">
            <label htmlFor="task_notes">Notes</label>
            <textarea id="task_notes" name="notes" value={form.notes} onChange={handleChange} rows={3} />
          </div>
          <div className="form-group">
            <label htmlFor="task-attachments">Attachments</label>
            <input id="task-attachments" type="file" multiple onChange={handleChange} />
            {attachmentError && <div className="form-error" role="alert">{attachmentError}</div>}
            {form.attachments.length > 0 && <div className="search-status">{form.attachments.length} attachment filename(s) captured</div>}
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={backToList}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Schedule Work'}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Team leaders and up book and change scheduled work (Gareth 2026-09-23).
 * Anyone else who follows a link here is told so instead of filling in a form
 * the server would refuse.
 */
function TaskForm() {
  const { user } = useAuth();
  const { id } = useParams();
  if (!getCapabilities(user).schedule_work) {
    return (
      <div className="task-form-page">
        <div className="form-header">
          {id ? (
            <Link to={`/tasks/${id}`} className="back-link">&larr; Back to the job</Link>
          ) : (
            <BackToList to="/tickets" also={['/tasks']} labels={WORK_LISTS} className="back-link">&larr; Jobs &amp; Tickets</BackToList>
          )}
          <h1>{id ? 'Edit Scheduled Work' : 'Schedule Engineer Work'}</h1>
        </div>
        <div className="form-error" role="alert">
          Only a team leader or manager can book or change scheduled work.
        </div>
      </div>
    );
  }
  return <TaskFormBody />;
}

export default TaskForm;
